'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/styleUtils';
import { PuzzleGrid, PuzzlePrompt } from '@/modules/captcha/components/PuzzleFrame';
import { randomInt } from '@/modules/captcha/shared/puzzle-utils';
import { useTapChallenge } from '@/modules/captcha/hooks/useTapChallenge';
import { shakeAnimation, tileBase, tileCorrect, tileIdle, tileWrong } from './puzzle.style';
import type { PuzzleProps } from './puzzle.types';

const CHIP_COUNT = 6;

/**
 * Six distinct two-digit bay numbers.
 *
 * Distinct is load-bearing: a duplicate would give the prompt two correct answers, one of which
 * is scored wrong. Two digits keeps every chip the same width so no chip stands out by size.
 */
function buildLabels(): string[] {
  const numbers = new Set<number>();
  while (numbers.size < CHIP_COUNT) {
    numbers.add(10 + randomInt(89));
  }
  return [...numbers].map((n) => `P${n}`);
}

/** Design 2j — find the named bay number among six. */
export const ParkingSlotPuzzle = ({ onSolved }: PuzzleProps) => {
  const t = useTranslations('Captcha');
  const [labels] = useState(buildLabels);
  const [answer] = useState(() => randomInt(CHIP_COUNT));
  const { wrongIndex, solved, pick } = useTapChallenge(answer, onSolved);

  return (
    <>
      <PuzzlePrompt>{t('findSlotPrompt', { label: labels[answer] })}</PuzzlePrompt>
      <PuzzleGrid columns={3} data-cy="captcha-parking-slot-puzzle">
        {labels.map((label, index) => {
          const isWrong = index === wrongIndex;
          const isCorrect = solved && index === answer;
          return (
            <button
              key={label}
              type="button"
              onClick={() => pick(index)}
              className={cn(
                tileBase,
                'tw:h-[48px] tw:font-mono tw:text-16 tw:font-bold',
                !isWrong && !isCorrect && cn(tileIdle, 'tw:text-font'),
                isCorrect && cn(tileCorrect, 'tw:text-success'),
                isWrong && cn(tileWrong, shakeAnimation, 'tw:text-error')
              )}
              data-cy={`captcha-slot-chip-${index}`}
            >
              {label}
            </button>
          );
        })}
      </PuzzleGrid>
    </>
  );
};

ParkingSlotPuzzle.displayName = 'ParkingSlotPuzzle';
